// O selo e o "Últ. Ponto" do cabeçalho do card seguem o BSeller na hora (API SAC, rota
// /pedidos-erp/{n}/rastreio-realtime), não o tabelão (Adnéia, 26/10/2026: "quero que os dois
// sigam o BSeller direto, deixando o tabelão").
// Por quê: o tabelão é cópia do banco da casa e atrasa. Na entrega 127719522, às 11h35 de
// 26/10, o tabelão ainda dizia "PROCESSAMENTO NA FILAL" (ponto da J&T das 05h45) e o BSeller
// já dizia "Entregue ao Cliente"; o ELO tinha movido o chamado pelo BSeller e o card desmentia.
// O tabelão só volta quando o BSeller não responde ou não traz esta entrega: aí o cabeçalho
// diz que é a cópia do banco. A API SAC manda só o dia do ponto, sem a hora.

function soNumero(valor) {
  return String(valor ?? '').trim().split('.')[0];
}

const PARTES_BR = new Intl.DateTimeFormat('pt-BR', {
  timeZone: 'America/Sao_Paulo', year: 'numeric', month: '2-digit', day: '2-digit',
  hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
});

// Data do ponto (API SAC) -> como a tela mostra (Adnéia, 04/11/2026: se a fonte só tem o dia,
// a tela mostra só o dia, sem hora inventada; se tem a hora, a hora certa, em Brasília).
// A API SAC manda o dia como a meia-noite de Brasília escrita em UTC: '2026-11-01T03:00:00.000Z'
// é 01/11 00:00 em Brasília. Antes a tela lia o "03:00" do texto e todo card dizia 03:00
// (120 de 120 entregas na amostra de 04/11). Agora:
// - com fuso (Z ou ±hh:mm): converte para Brasília; meia-noite de Brasília = só o dia;
// - sem fuso: os números valem como estão; 00:00 = só o dia;
// - outro formato volta como veio.
export function dataPontoBR(valor) {
  const s = String(valor ?? '').trim();
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::\d{2}(?:\.\d+)?)?(Z|[+-]\d{2}:?\d{2})?)?$/);
  if (!m) return s;
  let [ano, mes, dia, hora, minuto] = [m[1], m[2], m[3], m[4], m[5]];
  if (hora && m[6]) {
    const instante = new Date(s);
    if (Number.isNaN(instante.getTime())) return s;
    const p = Object.fromEntries(PARTES_BR.formatToParts(instante).map((x) => [x.type, x.value]));
    [ano, mes, dia, hora, minuto] = [p.year, p.month, p.day, p.hour, p.minute];
  }
  const diaBR = `${dia}/${mes}/${ano}`;
  return hora && `${hora}:${minuto}` !== '00:00' ? `${diaBR} ${hora}:${minuto}` : diaBR;
}

// Hora de um evento do histórico do rastreio (rota /historico-rastreio, tabela tracking_eventos
// do banco da casa, que vem do relatório ZBIQ0035 do BSeller). O ZBIQ0035 manda a hora de
// Brasília sem fuso ('01/11/2026 09:18:00') e o banco a guarda com o rótulo de UTC
// ('2026-11-01T09:18:00+00:00'). Prova de 04/11/2026, entrega 127958722: a NF saiu às
// 15:52:58 -03:00 na API REST do BSeller e às 15:52:58+00:00 no banco. Antes a tela convertia
// de novo e o histórico mostrava 3 horas a menos (entregue às 09:18, a tela dizia 06:18).
// Por isso aqui os números valem como estão, ignorando o rótulo. Se a carga do banco passar a
// gravar o fuso certo (é da big-data, do Allan), esta função tem de mudar junto.
export function horaRastreioBancoBR(valor) {
  const s = String(valor ?? '').trim();
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/);
  if (!m) return s;
  return `${m[3]}/${m[2]}/${m[1]} ${m[4]}:${m[5]}`;
}

// A entrega deste card na resposta do BSeller. A API devolve todas as entregas do pedido:
// vale só a de mesmo número; sem ela, nada (nunca a de outra entrega do mesmo pedido).
export function entregaDoCard(rastreio, numeroEntrega) {
  if (!rastreio || rastreio.status !== 'ok') return null;
  const alvo = soNumero(numeroEntrega);
  if (!alvo) return null;
  return (rastreio.entregas || []).find((e) => soNumero(e.id_entrega) === alvo) || null;
}

// A tela guarda a resposta junto do número que consultou: { numero, resposta } (resposta null =
// a consulta falhou). Resposta de outro número, ou nenhuma ainda, vale como "consultando": o card
// acabou de abrir ou trocou de pedido. Antes, por um instante, aparecia o tabelão com a marca
// "o BSeller não respondeu" sem o BSeller ter sido consultado (ressalva a da revisão de 26/10).
export function rastreioDoCard(guardado, numeroEntrega) {
  if (!guardado || !soNumero(numeroEntrega)) return undefined;
  if (soNumero(guardado.numero) !== soNumero(numeroEntrega)) return undefined;
  return guardado.resposta ?? null;
}

// rastreio: undefined = consultando o BSeller; null = a consulta falhou; objeto = a resposta.
// Devolve { status, data, fonte } com fonte 'bseller', 'consultando' ou 'tabelao'.
export function statusCabecalho(pedidoErp, rastreio) {
  const tabelao = {
    status: (pedidoErp && pedidoErp.status_pedido) || '',
    data: (pedidoErp && pedidoErp.data_status) || '',
  };
  if (rastreio === undefined) return { status: '', data: '', fonte: 'consultando' };
  const ent = entregaDoCard(rastreio, pedidoErp && pedidoErp.numero_pedido);
  if (ent && ent.ponto_descricao) {
    return { status: ent.ponto_descricao, data: dataPontoBR(ent.data_ponto), fonte: 'bseller' };
  }
  return { ...tabelao, fonte: 'tabelao' };
}
